import { useEffect, useMemo, useState, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { confirmDiscardIfUnsaved, registerUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { LoaderSymbol, BusyIcon } from '../../components/loading';
import { cascadeUrl, depsOf, isFieldLocked, PENDING_LABELS_KEY } from './moduleCrud';
import { cascadeLevels, fetchOptions, primeOptionCache, sharedOptionCache } from './cascadeFetch';
import type { CrudField, ModuleCrudConfig } from './moduleCrud';
import { useAutoFocusFirstControl } from '../../hooks/useAutoFocusFirstControl';
import { ComboSelect } from '../../components/ComboSelect';

interface Option { id: string; name: string }

interface Props {
  config: ModuleCrudConfig;
  /** null = create (New); a record id = open that record. */
  id: string | null;
  /** Values merged into the save payload but NOT rendered as fields - used to
   *  stamp a child record with its parent id, which the user must not be able to
   *  change from the child form. */
  fixedValues?: Record<string, unknown>;
  /** Read-only values from the OWNING record, seeded into this form's state so
   *  cascades can narrow by them. NOT rendered and NOT saved: the payload is
   *  built from `config.fields`, and a context key is not one. */
  contextValues?: Record<string, unknown>;
  /** When set, a CREATE hands the payload here INSTEAD of POSTing it.
   *
   *  Lets a child be filled in before its parent exists: the caller buffers the
   *  row and writes it once the parent has an id. Without this the child would
   *  have to be saved against a parent that is not there yet - which either fails
   *  or, worse, succeeds as an orphan. Edits of an existing row always POST. */
  onCapture?: (payload: Record<string, unknown>) => void;
  /** How the list opened the record. 'view' = READ-ONLY (a row click, matching
   *  the legacy XAF flow: row → DetailView to look at the data, with an Edit
   *  button to switch). 'edit' = editable straight away (the Edit pencil /
   *  toolbar Edit). Omitted falls back to view — or edit when the form sets
   *  `detailOpenInEdit` — matching <SettingsDetailPage>. Ignored for New, which
   *  is always editable. */
  mode?: 'view' | 'edit';
  /** The record's already-loaded row, to prefill an EDIT from instead of
   *  re-fetching it.
   *
   *  A list that renders a row necessarily holds it, and where that list returns
   *  the same shape as GET base/{id} (Permit Limits: both are PermitSetupDetail)
   *  the fetch is pure duplication - one round trip, ~300 ms against a remote
   *  database, spent behind a "Loading…" panel to arrive at data already in
   *  memory. Supplying it here opens the dialog populated.
   *
   *  Pass it ONLY where the list row genuinely carries every editable field; a
   *  partial row would silently blank what it omits. Omitted, the GET runs as
   *  before. */
  prefill?: Record<string, unknown>;
  onClose: () => void;
  onSaved: () => void;
}

/** Generic create/edit modal driven by a ModuleCrudConfig.  Reuses the shared
 *  ".qs-*" dialog chrome so it looks identical to the Query Samples modal.
 *
 *  Opt IN per form with `MODULE_CRUD[key].dialogEdit = true`; without that flag a
 *  form uses the full-page <SettingsDetailPage> (standing rule 12), which remains
 *  the default everywhere. Currently used by Settings ▸ Permit Library New ▸
 *  Permit Type New, whose approved design is this compact modal. */
export function ModuleCrudDialog({ config, id, mode, fixedValues, contextValues, onCapture, prefill, onClose, onSaved }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const editing = id !== null;
  // A NEW record is always editable; an existing one starts in whatever mode the
  // list opened it with, and the Edit button promotes view → edit in place.
  // An UNSPECIFIED mode falls back to view (or edit when the form opts in with
  // detailOpenInEdit) — the SAME rule <SettingsDetailPage> uses, so the popup and
  // the full-page detail never disagree about what a given open means.
  const initialEditable = !editing
    || (mode ?? (config.detailOpenInEdit ? 'edit' : 'view')) === 'edit';
  const [editable, setEditable] = useState(initialEditable);
  useEffect(() => { setEditable(initialEditable); }, [initialEditable, id]);
  const readOnly = !editable;
  // Context seeded UP FRONT, not in an effect: the cascade runs on the first
  // render, and a matrix arriving one render later would fetch the wider list
  // and then correct itself - the visible flicker we removed elsewhere.
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...contextValues }));
  const [options, setOptions] = useState<Record<string, Option[]>>({});
  // No spinner when the row was handed in - there is nothing to wait for.
  const [loading, setLoading] = useState(editing && !prefill);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // dialogStandard: after a save attempt, each empty required field says so under its own box
  // (single-field validation is inline, §18.6 tier 3) instead of one summary line.
  const [showMissing, setShowMissing] = useState(false);

  // Unsaved-edit guard (hooks/useUnsavedGuard.ts): true from the user's first
  // edit until saved. Closing by Cancel/Close OR by clicking the backdrop (a
  // single stray click used to discard everything typed) asks first.
  const dirtyRef = useRef(false);
  useEffect(() => registerUnsavedGuard(() => dirtyRef.current), []);
  const closeGuarded = () => {
    void confirmDiscardIfUnsaved().then((ok) => { if (ok) { dirtyRef.current = false; onClose(); } });
  };

  // Keep the context current if the owning record changes underneath (editing
  // the permit's Sample Matrix with the limit dialog open). Context keys are not
  // form fields, so this cannot clobber anything the user typed.
  const ctxSig = JSON.stringify(contextValues ?? {});
  useEffect(() => {
    if (!contextValues) return;
    setValues((s) => ({ ...s, ...contextValues }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxSig]);

  // Cascade wiring: map each PARENT key -> the child keys that declare dependsOn
  // it, so changing a parent can clear its descendants. Mirrors
  // <SettingsDetailPage>; the dialog previously had NO clearing at all, so
  // switching Parameter kept the old Method and Units - pairing one analyte's
  // method with another analyte, and feeding the Units cascade a mismatched pair.
  const childrenOf = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const f of config.fields) for (const k of depsOf(f)) (m[k] ??= []).push(f.key);
    return m;
  }, [config]);

  const set = (k: string, v: unknown) => {
    if (editable) dirtyRef.current = true;
    setValues((s) => {
      if (s[k] === v) return s;
      const next = { ...s, [k]: v };
      // Recursive: clearing Method must also clear Units, which depends on it.
      const clear = (key: string) => {
        for (const childKey of childrenOf[key] ?? []) {
          const cf = config.fields.find((f) => f.key === childKey);
          next[childKey] = cf?.type === 'multiselect' ? [] : '';
          clear(childKey);
        }
      };
      clear(k);
      return next;
    });
  };

  // Load select options once (per dialog open).
  //
  // STATIC options (field.options) are seeded first: without this a select whose
  // list is declared inline - Evaluation Basis, say - received an empty array and
  // rendered blank, because only optionsUrl fields were ever fetched.
  // <SettingsDetailPage> already did this; the popup did not, so the same config
  // behaved differently depending on which view opened it.
  useEffect(() => {
    const statics = config.fields.filter((f) => f.type === 'select' && !f.optionsUrl && f.options);
    if (statics.length) {
      setOptions((prev) => ({ ...prev, ...Object.fromEntries(statics.map((f) => [f.key, f.options!])) }));
    }
  }, [config]);

  // WARM THE CACHE IN ONE REQUEST (config.cachePrimeUrl). A cascade otherwise
  // costs a round trip per LEVEL, serially - picking a Permit Limit's Parameter
  // waits for Method and then for Units - which against a remote database is
  // most of the delay the user sees. Fetching every list the form could need up
  // front turns each later change into a memory read. Best-effort: unprimed, the
  // cascade behaves exactly as before.
  //
  // Awaited by DEEPER levels only (see the loop below), never by level 0.
  const primePromise = useRef<Promise<void> | null>(null);
  const primeUrl = config.cachePrimeUrl
    && (config.cachePrimeDependsOn ?? []).every((k) => String(values[k] ?? '').trim() !== '')
    ? cascadeUrl(config.cachePrimeUrl, { dependsOn: config.cachePrimeDependsOn }, values)
    : null;
  useEffect(() => {
    if (!primeUrl) return;
    primePromise.current = primeOptionCache(primeUrl, optionCache.current);
  }, [primeUrl]);

  useEffect(() => {
    let alive = true;
    // Cascading fields (dependsOn) are excluded: their URL carries the parent's
    // value, so they are loaded by the effect below instead.
    const selects = config.fields.filter((f) => f.type === 'select' && f.optionsUrl && !f.dependsOn);
    if (selects.length === 0) return;
    void (async () => {
      // READ THROUGH THE SHARED CACHE - but do NOT wait for the prime.
      //
      // These non-cascading lists (Limit Type, Operator) were fetched with a raw
      // api.get, outside fetchOptions and so outside the cache entirely. Routing
      // them through it means a prime that has already landed answers them for
      // free. Waiting on one still running would be a bad trade though: measured
      // 2026-09-02, cascade-prime is 900-1600 ms against ~250 ms for these
      // lists on their own.
      const pairs = await Promise.all(selects.map(async (f) =>
        [f.key, await fetchOptions(f.optionsUrl!, optionCache.current)] as const));
      // MERGE, never replace: the static options were seeded by the effect above,
      // and overwriting the whole map here wiped them the moment this fetch
      // resolved - the inline list appeared, then vanished a second later.
      if (alive) setOptions((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    })();
    return () => { alive = false; };
  }, [config, primeUrl]);

  // Cascade: (re)load each dependent select from
  // `optionsUrl?{dependsOn}={parentValue}` whenever its parent changes - e.g. the
  // Permit Limit's Units follows the chosen Parameter. Mirrors the identical
  // effect in <SettingsDetailPage>, so one config behaves the same whether it is
  // opened as a dialog or as a full-page detail.
  //
  // It runs on edit-PREFILL too, not just on user change: without that, opening
  // an existing row would leave the dependent select empty and its stored value
  // unresolvable.
  const dependents = useMemo(
    () => config.fields.filter((f) => f.type === 'select' && !!f.optionsUrl && !!f.dependsOn),
    [config],
  );
  const depSig = dependents
    .map((f) => `${f.key}=${depsOf(f).map((k) => String(values[k] ?? '')).join(',')}`)
    .join('|');

  // Preselect `defaultFirstOption` once the options are known — NEW records only,
  // and only into an EMPTY field. Mirrors <SettingsDetailPage> so one config
  // behaves the same in either view.
  useEffect(() => {
    if (editing) return;
    const wanted = config.fields.filter((f) => f.defaultFirstOption);
    if (wanted.length === 0) return;
    setValues((cur) => {
      const next = { ...cur };
      let touched = false;
      for (const f of wanted) {
        if (String(cur[f.key] ?? '').trim() !== '') continue;
        const first = (options[f.key] ?? [])[0];
        if (!first) continue;   // nothing to offer -> the field stays on None
        next[f.key] = first.id;
        touched = true;
      }
      return touched ? next : cur;
    });
  }, [options, editing, config]);

  // SHARED, not per-instance: this dialog unmounts on every close, so its own
  // Map started empty on each Edit and re-paid the prime plus a serial round
  // trip per cascade level against a remote database. The shared cache is
  // TTL-bounded, so reopening is instant without holding a stale list.
  const optionCache = useRef(sharedOptionCache);


  const levels = useMemo(() => cascadeLevels(dependents), [dependents]);
  useEffect(() => {
    if (dependents.length === 0) return;
    let alive = true;
    void (async () => {
      // A LOCAL working copy of the values. Each level's auto-selection is
      // written here before the next level builds its URL, which is the whole
      // point: Units asks with the Method that Method's level just picked,
      // instead of asking once without it and again with it.
      const work: Record<string, unknown> = { ...values };
      const fetched: Array<readonly [string, Option[]]> = [];
      const picked: Record<string, unknown> = {};

      for (let li = 0; li < levels.length; li++) {
        const level = levels[li]!;
        // LEVEL 0 NEVER WAITS FOR THE PRIME. Levels 1+ do.
        //
        // Measured 2026-09-02: cascade-prime costs 900-1600 ms (it carries the
        // whole matrix), while a level-0 list answers in ~250 ms on its own. So
        // making level 0 await it - which this briefly did - traded a 250 ms
        // fetch for a 1400 ms wait and left Limit Type and Operator blank
        // LONGER than before the prime existed.
        //
        // Not waiting costs nothing when the prime already landed: fetchOptions
        // reads the shared cache the prime filled and issues no request. It only
        // means that when the prime is still in flight, level 0 answers from the
        // endpoint rather than sitting behind the bundle.
        //
        // Levels 1+ still wait, because for them the prime replaces a round trip
        // EACH rather than duplicating one.
        if (li > 0 && primePromise.current) await primePromise.current;
        if (!alive) return;
        const pairs = await Promise.all(level.map(async (f) => {
          // Locked (enabledWhen unmet): ask for nothing and offer nothing, so the
          // disabled control cannot flash a list the user is not meant to pick from.
          if (isFieldLocked(f, work)) return [f.key, [] as Option[]] as const;
          const opts = await fetchOptions(cascadeUrl(f.optionsUrl!, f, work), optionCache.current);
          // KEEP A STORED VALUE VISIBLE. A narrowed list need not contain what the
          // record already holds - a Permit Limit saved when Units still offered
          // the whole table can sit on an analyte/method pair that records none.
          // Dropping it would render the control blank and silently clear the
          // value on the next save, so it is re-added, labelled from its
          // companion `<key>Name` (unitsId -> unitsName) the row already carries.
          const cur = String(work[f.key] ?? '').trim();
          if (cur && !opts.some((o) => o.id === cur)) {
            const nameKey = f.key.replace(/Id$/, '') + 'Name';
            const label = String(values[nameKey] ?? '').trim();
            const withCurrent: Option[] = [{ id: cur, name: label || '(current)' }, ...opts];
            return [f.key, withCurrent] as const;
          }
          return [f.key, opts] as const;
        }));
        if (!alive) return;
        fetched.push(...pairs);

        for (const [key, opts] of pairs) {
          const f = config.fields.find((x) => x.key === key);
          if (!f || opts.length === 0) continue;
          // Only ever auto-select from a list that was actually NARROWED. With NO
          // parent set, cascadeUrl asks unfiltered and the list is the whole table -
          // binding its first row would pick an arbitrary value the user never implied.
          // Default: SOME dep non-empty (optional deps like sampleMatrixId, which is
          // always set from context, still allow Units to bind once parameter is chosen).
          // autoSelectRequires: ALL listed keys must be non-empty — used by Method so
          // that the always-present sampleMatrixId context value alone does not trigger
          // an auto-pick before the user has chosen a Parameter.
          const autoTriggerKeys = f.autoSelectRequires ?? null;
          const isTriggered = autoTriggerKeys
            ? autoTriggerKeys.every((k) => String(work[k] ?? '').trim() !== '')
            : depsOf(f).some((k) => String(work[k] ?? '').trim() !== '');
          if (!isTriggered) continue;
          // autoSelectFirst takes any first option; autoSelectSingle only an
          // unambiguous one. Either way the field must still be EMPTY - that guard
          // is what stops this stamping over a value the user just chose, or over
          // an existing record's value while the cascade re-runs on edit-prefill.
          if (!(f.autoSelectFirst || (f.autoSelectSingle && opts.length === 1))) continue;
          if (String(work[key] ?? '').trim() !== '') continue;
          work[key] = opts[0]!.id;
          picked[key] = opts[0]!.id;
        }
      }

      if (!alive) return;
      setOptions((prev) => ({ ...prev, ...Object.fromEntries(fetched) }));
      // Re-check emptiness against the CURRENT state, not the snapshot this pass
      // started from - the user may have typed while the requests were in flight.
      setValues((cur) => {
        const next = { ...cur };
        let touched = false;
        for (const [k, v] of Object.entries(picked)) {
          if (String(cur[k] ?? '').trim() !== '') continue;
          next[k] = v;
          touched = true;
        }
        return touched ? next : cur;
      });
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depSig, dependents, levels]);

  // Fields shown for THIS mode. `editOnly` fields are hidden on New (e.g.
  // Active, which the DB defaults on create) — hidden everywhere that matters:
  // render, defaults, the required check, and the save payload.
  // A field pinned by `fixedValues` (a child's parent FK, e.g. the certificate's
  // owning Instrument) is NOT rendered — it is stamped from the parent on save,
  // and the legacy child popup never shows it. Matches the fixedValues contract.
  const visibleFields = useMemo(
    () => config.fields.filter((f) => (editing || !f.editOnly) && !(fixedValues && f.key in fixedValues)),
    [config.fields, editing, fixedValues],
  );

  // New: seed from each field's defaultValue (e.g. Active ticked by default),
  // matching what <SettingsDetailPage> does for the full-page detail. Without
  // this a new record starts with every checkbox cleared.
  useEffect(() => {
    if (editing) return;
    const seed: Record<string, unknown> = {};
    for (const f of visibleFields) {
      if (f.defaultValue !== undefined) seed[f.key] = f.defaultValue;
    }
    // Merge: seed only sets keys that aren't already present (contextValues like
    // sampleMatrixId were seeded in useState and must not be wiped).
    setValues((prev) => ({ ...seed, ...prev }));
  }, [visibleFields, editing]);

  // Edit: prefill from the row the list already holds, else GET base/{id}.
  useEffect(() => {
    if (!editing) return;

    // MERGE, never replace. The mapped record is built from config.fields, so it
    // cannot contain the parent CONTEXT keys (sampleMatrixId is the permit's,
    // not the limit's) - a hard setValues(next) silently dropped them. That is
    // not cosmetic: with no matrix the prime is skipped entirely on Edit and
    // every cascade URL loses its ?sampleMatrixId=, so the lists come back
    // UNFILTERED - slower, and wrong (%CV returns to Parameter, and Units loses
    // the mg/kg-vs-mg/L narrowing). The record still wins for every real field.
    const apply = (d: Record<string, unknown>) => {
      const next: Record<string, unknown> = {};
      for (const f of config.fields) {
        const raw = d[f.key];
        if (f.type === 'date') next[f.key] = toDateInput(raw);
        else if (f.type === 'checkbox') next[f.key] = Boolean(raw);
        // A select's <option> values are option IDS, so when the API returns the
        // stored FK under a separate key (idKey: vendorOid) that is what the form
        // value must be — `key` holds the display name, or nothing. Same fix as
        // SettingsDetailPage; no dialog config uses idKey yet, so this is a no-op
        // today and a trap removed for the next one that does.
        else if (f.type === 'select' && f.idKey) {
          const id = d[f.idKey];
          next[f.key] = id == null ? '' : String(id);
        }
        else if (f.type === 'select') next[f.key] = raw == null ? '' : String(raw);
        else next[f.key] = raw == null ? '' : String(raw);

        // CARRY THE COMPANION DISPLAY NAME (unitsId -> unitsName). Building
        // `next` from config.fields alone dropped it, and the cascade below
        // needs it to LABEL a stored value its narrowed list does not contain:
        // without it the control fell back to the literal "(current)" instead
        // of the unit the row plainly shows. Not posted - the payload is built
        // from the declared fields only.
        if (f.type === 'select') {
          const nameKey = `${f.key.replace(/Id$/, '')}Name`;
          if (d[nameKey] != null) next[nameKey] = String(d[nameKey]);
        }
        // A file field carries its display name in a sibling key (attachmentFileName)
        // so the download link labels the existing file on Edit.
        if (f.type === 'file' && f.fileNameKey && d[f.fileNameKey] != null) next[f.fileNameKey] = String(d[f.fileNameKey]);
      }
      setValues((prev) => ({ ...prev, ...next }));
    };

    // The list row IS the record here - no request, no "Loading…" panel.
    if (prefill) { setError(null); apply(prefill); setLoading(false); return; }

    let alive = true;
    setLoading(true); setError(null);
    api.get<{ data?: Record<string, unknown> }>(`${config.basePath}/${id}`)
      .then((res) => { if (alive) apply(res.data.data ?? {}); })
      .catch(() => { if (alive) setError(`Could not load this ${config.entityLabel.toLowerCase()}.`); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // `prefill` is the row captured when the dialog opened; it does not change
    // underneath and is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, id, editing]);

  const missingRequired = useMemo(
    () => visibleFields.filter(
      (f) => f.required && f.type !== 'readonly'
        && String(values[f.key] ?? '').trim() === '',
    ).map((f) => f.label),
    [visibleFields, values],
  );

  const save = async () => {
    if (missingRequired.length > 0) {
      if (config.dialogStandard) { setShowMissing(true); setError(null); }
      else setError(`Required: ${missingRequired.join(', ')}.`);
      return;
    }
    setSaving(true); setError(null);
    const payload: Record<string, unknown> = {};
    for (const f of visibleFields) {
      if (f.type === 'readonly') continue;
      const v = values[f.key];
      // A select with idKey posts the picked id under THAT key — what the API
      // binds. Same rule as SettingsDetailPage.buildPayload.
      const outKey = f.type === 'select' && f.idKey ? f.idKey : f.key;
      if (f.type === 'checkbox') payload[outKey] = Boolean(v);
      else if (f.type === 'number') {
        // Real JSON number - see the same branch in SettingsDetailPage.
        const s = v == null ? '' : String(v).trim();
        const n = s === '' ? null : Number(s);
        payload[outKey] = n != null && Number.isFinite(n) ? n : null;
      }
      else {
        const s = v == null ? '' : String(v).trim();
        payload[outKey] = s === '' ? null : s;
      }
    }
    // Parent linkage (and anything else the caller pins) always wins over the
    // form's own values, so a child cannot be re-parented from its own dialog.
    Object.assign(payload, fixedValues ?? {});

    // DISPLAY LABELS for a row that will be buffered rather than POSTed. The
    // payload carries FK Oids while the child grid renders names, so without
    // these every FK-backed cell shows blank until the parent is saved. Both
    // spellings are emitted because the grids use both conventions
    // (parameterId -> parameterName, operator -> operatorDisplay); the extra
    // key is inert wherever a grid does not ask for it.
    const labels: Record<string, string> = {};
    for (const f of visibleFields) {
      if (f.type !== 'select') continue;
      const v = String(values[f.key] ?? '').trim();
      if (v === '') continue;
      const opts = f.options ?? options[f.key] ?? [];
      const name = opts.find((o) => o.id === v)?.name;
      if (!name) continue;
      const base = f.key.replace(/Id$/, '');
      labels[`${base}Name`] = name;
      labels[`${base}Display`] = name;
    }

    try {
      // Shared ALPACA-style save flow: "Do you want to Save?" → save via the
      // existing api.put/post → "Saved successfully." → close (onSaved).
      await runSave({
        save: async () => {
          if (editing) { await api.put(`${config.basePath}/${id}`, payload); return; }
          // Parent not saved yet - hand the row back to be written later, with
          // the labels it needs to render meanwhile.
          if (onCapture) { onCapture({ ...payload, [PENDING_LABELS_KEY]: labels }); return; }
          await api.post(config.basePath!, payload);
        },
        successMessage: `${config.entityLabel} saved successfully.`,
        onSaved: () => { dirtyRef.current = false; onSaved(); },
        onError: (e) => {
          const msg = (e as { response?: { data?: { error?: { message?: string } } } })
            .response?.data?.error?.message ?? 'Save failed.';
          notify.error(msg);
        },
      });
    } finally {
      setSaving(false);
    }
  };

  // Legacy XAF popups lay their fields out in TWO columns, filled top-to-bottom
  // then left-to-right (column-major). Opt-in per config via `columnMajor`, so
  // single-column dialogs (Permit Type, etc.) are unchanged. Widen the dialog to
  // fit two columns.
  const twoCol = config.columnMajor === true;
  // Fields the form keeps but does not draw (CrudField.hideInLayout): still loaded, cascaded and
  // posted through `visibleFields`; only the layout skips them.
  const shownFields = visibleFields.filter((f) => !f.hideInLayout);
  const colRows = config.columnRows ?? Math.ceil(shownFields.length / 2);

  // Mouse-free operation (ADR-0013, standing rule 14): focus the first control when
  // the popup opens (once the fields render, not the "Loading…" spinner), close on
  // Esc, and return focus to the opener (the New/Edit button) on close — so this
  // bespoke child-grid popup behaves like the shared <AppDialog> (user report
  // 2026-09-21: "focus is not coming to the form when it opens").
  const bodyRef = useRef<HTMLDivElement>(null);
  useAutoFocusFirstControl(bodyRef, !loading);
  const restoreRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;
    return () => {
      const el = restoreRef.current;
      if (el && el.isConnected && typeof el.focus === 'function') el.focus({ preventScroll: true });
    };
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Esc closes THROUGH the unsaved-edit guard (confirms before discarding), same
      // as the backdrop; skip if something inside already handled it (an open combo,
      // or a confirm dialog stacked on top — its capture-phase handler preventDefaults).
      if (e.key === 'Escape' && !e.defaultPrevented) { closeGuarded(); return; }
      // Ctrl/⌘+S saves — clicks the footer Save button (respects its disabled state),
      // even while a field is focused; the global store yields to this aria-modal, so
      // the popup owns its own Ctrl+S (user report 2026-09-21). Not in view/read-only
      // mode (there the solid button is "Edit", not Save).
      if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && !readOnly) {
        const saveBtn = bodyRef.current?.closest('.qs-dialog')
          ?.querySelector<HTMLButtonElement>('.qs-footer .ca-btn-solid:not([disabled])');
        if (saveBtn) { e.preventDefault(); saveBtn.click(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeGuarded, readOnly]);

  return (
    <div className="qs-overlay" onMouseDown={closeGuarded}>
      <div className={`qs-dialog${config.dialogAccent ? ' mcd-accent' : ''}`} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()} style={{ width: twoCol ? 760 : 560, maxWidth: '95vw' }}>
        <div className="qs-titlebar">
          <span>
            {/* A fixed `dialogTitle` (legacy DetailView caption) is used verbatim
                in every mode; otherwise New/Edit/{entityLabel}. */}
            {config.dialogTitle
              ? config.dialogTitle
              : !editing ? `New ${config.entityLabel}`
              : readOnly ? config.entityLabel
              : `Edit ${config.entityLabel}`}
          </span>
          {/* Popup standard (dialogAccent): a round close button in the title bar. */}
          {config.dialogAccent && (
            <button type="button" className="mcd-x" title="Close (Esc)" aria-label="Close" onClick={closeGuarded}>
              <i className="ti ti-x" />
            </button>
          )}
        </div>

        <div className="qs-body" ref={bodyRef} style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
              <LoaderSymbol size={18} /> Loading…
            </div>
          ) : (<>
            {config.dialogHeading && <div className="mcd-heading">{config.dialogHeading}</div>}
            {/* squareControls (the same opt-in the full-page detail uses) also squares this popup's boxes and
                drops their tint — the shared qs-grid--square style (owner request 2026-10-16). */}
            <div className={`qs-grid-wide mcd-fields${config.dialogStandard ? ' mcd-std' : ''}${config.squareControls ? ' qs-grid--square' : ''}`}
              style={twoCol
                ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: `repeat(${colRows}, auto)`,
                    gridAutoFlow: 'column', columnGap: 20, rowGap: 12 }
                : { display: 'flex', flexDirection: 'column', gap: 12 }}>
              {shownFields.map((f) => (
                f.type === 'file' ? (
                  <DialogFileField key={f.key} field={f}
                    oid={values[f.key] == null ? '' : String(values[f.key])}
                    fileName={f.fileNameKey ? String(values[f.fileNameKey] ?? '') : ''}
                    readOnly={readOnly}
                    onChange={(oid, name) => setValues((s) => ({
                      ...s, [f.key]: oid, ...(f.fileNameKey ? { [f.fileNameKey]: name } : {}),
                    }))} />
                ) : (
                  <Field key={f.key} field={f} value={values[f.key]}
                    readOnly={readOnly || !!f.readOnly || isFieldLocked(f, values) || (!!f.lockOnEdit && editing)}
                    options={options[f.key] ?? []} onChange={(v) => set(f.key, v)}
                    missing={showMissing && !!f.required && f.type !== 'readonly'
                      && String(values[f.key] ?? '').trim() === ''} />
                )
              ))}
              {error && (
                <div className="mcd-error" style={{ color: '#b71c1c', fontSize: 12, padding: '4px 2px' }}>
                  <i className="ti ti-alert-triangle" /> {error}
                </div>
              )}
            </div>
          </>)}
        </div>

        {/* VIEW mode — Close + Edit (promotes to editable without reopening).
            EDIT / NEW — Cancel → Save, right-aligned, Save solid: the approved
            prototype's button order ("Save" on both New and Edit, never
            "Create"). */}
        <div className="qs-footer qs-footer-end">
          {readOnly ? (
            <>
              <button className="ca-btn" onClick={closeGuarded}>Close</button>
              <button className="ca-btn ca-btn-solid" onClick={() => setEditable(true)}
                disabled={loading}>
                <span className="ti ti-pencil" /> Edit
              </button>
            </>
          ) : (
            <>
              {/* Popup standard (dialogAccent): OK first, then Cancel — as every standard popup. */}
              {!config.dialogAccent && <button className="ca-btn" onClick={closeGuarded} disabled={saving}>Cancel</button>}
              <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving || loading}>
                <BusyIcon busy={saving} icon="ti-device-floppy" /> {config.dialogSaveLabel ?? 'Save'}
              </button>
              {config.dialogAccent && (
                <button className="ca-btn" onClick={closeGuarded} disabled={saving}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ field, value, options, readOnly = false, missing = false, onChange }: {
  field: CrudField; value: unknown; options: Option[]; readOnly?: boolean;
  /** Required, empty, and a save was attempted — say so under the box. */
  missing?: boolean;
  onChange: (v: unknown) => void;
}) {
  const id = `crud-${field.key}`;
  // `disabled` (not just readOnly) so selects and checkboxes are inert too, and
  // the browser gives every editor the same greyed affordance in view mode.
  // `placeholder` is passed through here so a config behaves the same in the
  // dialog as in <SettingsDetailPage>; the dialog previously ignored it
  // entirely. Harmless on the controls that have no use for it (a select
  // ignores it; a readonly field always renders a value).
  const common = { id, className: 'qs-input', disabled: readOnly,
    placeholder: field.placeholder, style: { width: '100%' } as const };

  return (
    <div className="qs-field">
      {/* Required marker uses the SHARED `.req-star` (theme/boStandard.css,
          var(--ai-red)) — the same red asterisk the grid headers and the
          full-page detail use, so "required" reads identically everywhere
          (BO UI Standard §4). */}
      <label className="qs-label" htmlFor={id}>
        {field.label}
        {field.required && !field.hideRequiredMark && <span className="req-star"> *</span>}
      </label>
      {field.type === 'readonly' ? (
        <input {...common} type="text" readOnly disabled
          value={value == null || value === '' ? '(auto)' : String(value)} />
      ) : field.type === 'checkbox' ? (
        // Bare checkbox — the caption already sits in the left .qs-label column,
        // so repeating it beside the box would print the label twice.
        <input id={id} type="checkbox" checked={Boolean(value)} disabled={readOnly}
          onChange={(e) => onChange(e.target.checked)} />
      ) : field.type === 'select' && field.radioInDialog ? (
        // Opt-in radio group (CrudField.radioInDialog) — same options and value as the select.
        <div className="qs-radios" role="radiogroup" aria-label={field.label}>
          {options.map((o) => (
            <label key={o.id} className="qs-radio">
              <input type="radio" name={id} value={o.id} disabled={readOnly}
                checked={String(value ?? '') === String(o.id)} onChange={() => onChange(String(o.id))} />
              {o.name}
            </label>
          ))}
        </div>
      ) : field.type === 'select' && field.comboList ? (
        // Opt-in (CrudField.comboList), as on <SettingsDetailPage>: a native <select> list cannot be
        // height-limited, so a long one covers the popup; the shared combo is capped and searchable.
        <ComboSelect
          className="mcd-combo"
          value={value == null ? '' : String(value)}
          options={options.map((o) => ({ value: String(o.id), label: o.name }))}
          placeholder={field.noneLabel ?? '— None —'}
          disabled={readOnly}
          clearable={!field.hideBlankOption}
          onChange={(v) => onChange(v)}
        />
      ) : field.type === 'select' ? (
        <select {...common} value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}>
          {!field.hideBlankOption && (
            <option value="">{field.noneLabel ?? '— None —'}</option>
          )}
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      ) : field.type === 'number' ? (
        <input {...common} type="number"
          min={field.min} max={field.max} step={field.step ?? 1}
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)} />
      ) : field.type === 'date' ? (
        <input {...common} type="date" value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)} />
      ) : field.type === 'textarea' ? (
        <textarea {...common} rows={3} maxLength={field.maxLength}
          value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input {...common} type="text" maxLength={field.maxLength}
          value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
      )}
      {field.hint && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{field.hint}</div>}
      {missing && <div className="mcd-field-err" role="alert">{field.label} is required.</div>}
    </div>
  );
}

/** File field for the dialog (legacy Attachment "Browse…"): uploads to
 *  field.uploadUrl → FileData Oid, downloads from field.downloadUrlBase/{oid}/download.
 *  Mirrors <SettingsDetailPage>'s FileField so a config behaves the same in either
 *  view. Stores the FileData Oid in the field key and the filename in fileNameKey. */
function DialogFileField({ field, oid, fileName, readOnly, onChange }: {
  field: CrudField; oid: string; fileName: string; readOnly: boolean;
  onChange: (oid: string, fileName: string) => void;
}) {
  const notify = useNotify();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const hasFile = oid !== '' && oid != null;

  const download = async () => {
    if (!hasFile || !field.downloadUrlBase) return;
    try {
      const res = await api.get(`${field.downloadUrlBase}/${oid}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName || 'attachment';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      notify.error('Could not download the file.');
    }
  };

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !field.uploadUrl) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{ data?: { fileDataOid?: string; fileName?: string } }>(field.uploadUrl, fd);
      const newOid = res.data.data?.fileDataOid;
      if (!newOid) throw new Error('no oid');
      onChange(newOid, res.data.data?.fileName ?? file.name);
    } catch {
      notify.error('Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="qs-field">
      <label className="qs-label">
        {field.label}{field.required && <span className="req-star"> *</span>}
      </label>
      <div className="sd-file">
        {hasFile ? (
          <button type="button" className="sd-file-link" onClick={download} title="Download">
            <i className="ti ti-file-text" /> {fileName || 'attachment'}
          </button>
        ) : (
          <span className="sd-file-none">N/A</span>
        )}
        {!readOnly && (
          <span className="sd-file-actions">
            <input ref={fileRef} type="file" hidden onChange={pick} />
            <button type="button" className="sd-file-btn" disabled={busy}
              onClick={() => fileRef.current?.click()} title={hasFile ? 'Replace' : 'Browse'}>
              <BusyIcon busy={busy} icon="ti-upload" /> {hasFile ? 'Replace' : 'Browse…'}
            </button>
            {hasFile && (
              <button type="button" className="sd-file-btn danger" disabled={busy}
                onClick={() => onChange('', '')} title="Clear">
                <i className="ti ti-trash" /> Clear
              </button>
            )}
          </span>
        )}
      </div>
      {field.hint && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{field.hint}</div>}
    </div>
  );
}

/** ISO / date string → yyyy-MM-dd for <input type=date>. */
function toDateInput(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
